// Get screen dimensions
const getScreenDimensions = () => {
  return {
    width: window.screen.width.toString(),
    height: window.screen.height.toString(),
  };
};

// Get timezone
const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Get all possible IP addresses
const getAllIPs = async () => {
  try {
    // Get public IPs
    const publicIPs = {
      ipv4: null,
      ipv6: null,
    };

    // Try multiple services for public IP detection
    const services = [
      "https://api.ipify.org?format=json",
      "https://api64.ipify.org?format=json", // IPv6
    ];

    for (const service of services) {
      try {
        const response = await fetch(service);
        const data = await response.json();
        if (data.ip) {
          if (data.ip.includes(":")) {
            publicIPs.ipv6 = data.ip;
          } else {
            publicIPs.ipv4 = data.ip;
          }
        }
      } catch (error) {
        console.error(`Error fetching IP from ${service}:`, error);
      }
    }

    // Get local IPs using WebRTC
    const localIPs = {
      ipv4: null,
      ipv6: null,
    };

    try {
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ],
      });

      pc.createDataChannel("");

      // Create a promise that resolves when we have all IPs or timeout
      const getLocalIPs = new Promise((resolve) => {
        const timeout = setTimeout(() => {
          pc.close();
          resolve(localIPs);
        }, 10000); // Increased timeout to 10 seconds

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            const candidate = event.candidate.candidate;

            // Extract IP from candidate string
            const ipv4Regex =
              /([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3})/;
            const ipv6Regex = /([0-9a-fA-F]{1,4}(?::[0-9a-fA-F]{1,4}){7})/;

            const ipv4Match = candidate.match(ipv4Regex);
            const ipv6Match = candidate.match(ipv6Regex);

            if (ipv4Match && !localIPs.ipv4) {
              const ip = ipv4Match[1];
              if (ip && ip !== "0.0.0.0" && ip !== "127.0.0.1") {
                localIPs.ipv4 = ip;
              }
            }
            if (ipv6Match && !localIPs.ipv6) {
              const ip = ipv6Match[1];
              if (ip && ip !== "::1") {
                localIPs.ipv6 = ip;
              }
            }

            // If we have both IPs, resolve early
            if (localIPs.ipv4 && localIPs.ipv6) {
              clearTimeout(timeout);
              pc.close();
              resolve(localIPs);
            }
          } else {
            // No more candidates
            clearTimeout(timeout);
            pc.close();
            resolve(localIPs);
          }
        };
      });

      // Create and set local description
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for IPs to be collected
      await getLocalIPs;
    } catch (error) {
      console.error("Error getting local IPs:", error);
    }

    return {
      public: publicIPs,
      local: localIPs,
    };
  } catch (error) {
    console.error("Error getting IPs:", error);
    return {
      public: { ipv4: null, ipv6: null },
      local: { ipv4: null, ipv6: null },
    };
  }
};

// Get IP-based geolocation
const getGeolocation = async () => {
  try {
    const ipData = await getAllIPs();

    // Try to get geolocation data using the public IP
    let geolocationData = {
      latitude: "",
      longitude: "",
      country: "",
      region: "",
      city: "",
    };

    if (ipData.public.ipv4 || ipData.public.ipv6) {
      try {
        const ipToUse = ipData.public.ipv4 || ipData.public.ipv6;
        const response = await fetch(`https://ipapi.co/${ipToUse}/json/`);
        const data = await response.json();

        if (data && !data.error) {
          geolocationData = {
            latitude: data.latitude?.toString() || "",
            longitude: data.longitude?.toString() || "",
            country: data.country_name || "",
            region: data.region || "",
            city: data.city || "",
          };
        }
      } catch (error) {
        console.error("Error fetching geolocation:", error);
      }
    }

    return {
      ...geolocationData,
      publicIpv4: ipData.public.ipv4 || "",
      publicIpv6: ipData.public.ipv6 || "",
      localIpv4: ipData.local.ipv4 || "",
      localIpv6: ipData.local.ipv6 || "",
    };
  } catch (error) {
    console.error("IP geolocation error:", error);
    return {
      latitude: "",
      longitude: "",
      country: "",
      region: "",
      city: "",
      publicIpv4: "",
      publicIpv6: "",
      localIpv4: "",
      localIpv6: "",
    };
  }
};

// Get device info
export const getDeviceInfo = async () => {
  const screen = getScreenDimensions();
  const timezone = getTimezone();
  const location = await getGeolocation();

  const deviceInfo = {
    "x-screen-width": screen.width,
    "x-screen-height": screen.height,
    "x-timezone": timezone,
    "x-latitude": location.latitude || "",
    "x-longitude": location.longitude || "",
    "x-country": location.country || "",
    "x-region": location.region || "",
    "x-city": location.city || "",
    "x-public-ipv4": location.publicIpv4 || "",
    "x-public-ipv6": location.publicIpv6 || "",
    "x-local-ipv4": location.localIpv4 || "",
    "x-local-ipv6": location.localIpv6 || "",
  };

  return deviceInfo;
};
